import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CreateResourceCapacityRequest,
  ResourceCapacitiesResponseDto,
  ResourceCapacityDto,
  ResourceCapacityOverride,
  UpsertResourceCapacityOverrideRequest,
} from '../models/capacity.models';

@Injectable({
  providedIn: 'root',
})
export class CapacityService {
  private http = inject(HttpClient);

  private readonly baseUrl = `${environment.apiUrl}/api/capacity`;

  /**
   * Get capacity records for a resource
   * @param resourceId The employee ID
   * @param date Optional start date (defaults to current date on server)
   * @param numberOfIntervals Number of intervals to retrieve (default 1)
   * @returns Observable of ResourceCapacitiesResponseDto
   */
  getCapacities(
    resourceId: string,
    date?: string,
    numberOfIntervals: number = 1
  ): Observable<ResourceCapacitiesResponseDto> {
    let params = new HttpParams().set(
      'numberOfIntervals',
      numberOfIntervals.toString()
    );

    if (date) {
      params = params.set('date', date);
    }

    return this.http.get<ResourceCapacitiesResponseDto>(
      `${this.baseUrl}/${resourceId}`,
      { params }
    );
  }

  /**
   * Bulk upsert capacity records
   * @param requests Array of capacity records to create or update
   * @returns Observable of the created/updated capacity records
   */
  bulkUpsertCapacities(
    requests: CreateResourceCapacityRequest[]
  ): Observable<ResourceCapacityDto[]> {
    return this.http.post<ResourceCapacityDto[]>(
      `${this.baseUrl}/bulk`,
      requests
    );
  }

  /**
   * Get all active capacity overrides for an employee
   * @param employeeId The employee ID
   * @returns Observable of the employee's capacity overrides (empty if none exist)
   */
  getCapacityOverrides(
    employeeId: string
  ): Observable<ResourceCapacityOverride[]> {
    return this.http.get<ResourceCapacityOverride[]>(
      `${this.baseUrl}/overrides/${employeeId}`
    );
  }

  /**
   * Create or update capacity override for an employee
   * @param employeeId The employee ID
   * @param capacityStatusCode The capacity status code to set
   * @returns Observable of the created/updated override
   */
  upsertCapacityOverride(
    employeeId: string,
    capacityStatusCode: string
  ): Observable<ResourceCapacityOverride> {
    const request: UpsertResourceCapacityOverrideRequest = {
      employeeId,
      capacityStatusCode,
    };
    return this.http.post<ResourceCapacityOverride>(
      `${this.baseUrl}/overrides`,
      request
    );
  }

  /**
   * Cancel the active capacity override for an employee
   * Sends a null capacityStatusCode to end the current override
   * @param employeeId The employee ID
   * @returns Observable of void
   */
  cancelCapacityOverride(employeeId: string): Observable<void> {
    const request: UpsertResourceCapacityOverrideRequest = {
      employeeId,
      capacityStatusCode: null,
    };
    return this.http.post<void>(`${this.baseUrl}/overrides`, request);
  }
}
